/**
 * Severity presentation for schema-driven values.
 *
 * The backend always sends a `severity` and, on the capability options, a
 * `glyph` alongside it. Both are rendered together everywhere in this feature —
 * the shape carries the meaning and the colour reinforces it, so the console
 * stays readable to anyone who can't distinguish the reds from the greens.
 * Colour-only status is the one thing not to do here.
 */
import { Glyph, Severity } from "./configTypes";
import { tokens } from "./tokens";

export interface SeverityStyle {
  bg: string;
  color: string;
  border: string;
}

const STYLES: Record<Severity, SeverityStyle> = {
  ok: {
    bg: "rgba(10,143,0,0.10)",
    color: tokens.readyText,
    border: "rgba(10,143,0,0.30)",
  },
  warn: {
    bg: "rgba(255,150,0,0.12)",
    color: tokens.majorText,
    border: "rgba(255,150,0,0.40)",
  },
  bad: {
    bg: "rgba(229,72,77,0.10)",
    color: tokens.groundedText,
    border: "rgba(229,72,77,0.35)",
  },
  neutral: {
    bg: tokens.offWhite,
    color: tokens.slate,
    border: tokens.line,
  },
};

/** Unknown severities read as neutral rather than throwing — a value written
 *  before a vocabulary change must still render. */
export const severityStyle = (severity?: Severity | string): SeverityStyle =>
  STYLES[(severity as Severity) ?? "neutral"] ?? STYLES.neutral;

const GLYPHS: Record<Glyph, string> = {
  check: "✓",
  half: "◐",
  cross: "✕",
  dash: "–",
};

export const glyphChar = (glyph?: Glyph | string): string =>
  GLYPHS[(glyph as Glyph) ?? "dash"] ?? "";
